// React
import { useEffect, useCallback } from 'react'

// Hooks
import { useSettings } from 'shared/utils/useSettings'
import { useSearchParam } from 'shared/utils/useSearchParam'
import { useSelector } from 'react-redux'

// Shared
import CartActions from 'store/v2/redux/actions/cart'
import ClientActions from 'store/v2/redux/actions/client'

/**
 * @typedef {Object} DatePicker
 *
 * @property {boolean} isOpen - A boolean indicating whether the date picker is open or not.
 * @property {() => void} open - A function to open the date picker.
 * @property {() => void} close - A function to close the date picker. The behavior varies depending on the settings.
 * @property {'show_cart' | 'checkout'} behavior - A string indicating the behavior of the date picker set in the settings.
 */

/**
 * @function useDatePicker
 *
 * @description A hook that returns the date picker state, behavior and utility functions.
 *
 * @returns {DatePicker}
 */
export const useDatePicker = () => {
  const shouldOpenPicker = useSearchParam('open-picker')

  const behavior = useSettings('store.behaviors.add_button')

  const isOpen = useSelector((state) => state.client.modalPickerOpen)

  const open = useCallback(() => {
    ClientActions.toggleModalPicker(true)
  }, [])

  const close = useCallback(() => {
    switch (behavior) {
      case 'show_cart': {
        ClientActions.toggleModalPicker(false)
        break
      }
      case 'checkout': {
        CartActions.clear()
        ClientActions.toggleModalPicker(false)
        break
      }
    }
  }, [behavior])

  // Effect to check if we should open picker based on the URL search param
  useEffect(() => {
    if (!shouldOpenPicker) return

    open()
  }, [])

  return {
    isOpen,
    open,
    close,
    behavior
  }
}
